import { useEffect, useState } from 'react'
import { api } from '../api'
import type { DayStat, Topic } from '../types'
import { ApiError } from '../types'
import { accText, accTone, Bar, TopBar } from '../components/ui'
import { Between, Label, Note, Screen, Shell, Tile } from '../components/shell'
import { Icon } from '../components/Icon'

/** Số câu mặc định mỗi lần luyện. 20 đủ dài để có giá trị, ngắn để không
 *  bỏ dở giữa đường. */
const COUNTS = [10, 20, 40, 60]

/** Mục tiêu ngày. Số câu này là hằng số thiết kế, không phải dữ liệu. */
const DAILY_GOAL = 20

export function Home({
  user,
  topics,
  dueForReview,
  byDay,
  onPractice,
  onGoProgress,
  onGoWrong,
  onGoAccount,
  onGoPay,
}: {
  user: string
  topics: Topic[]
  dueForReview: number
  byDay: DayStat[]
  onPractice: () => void
  onGoProgress: () => void
  onGoWrong: () => void
  onGoAccount: () => void
  onGoPay: () => void
}) {
  // Đề mục cần ôn: đã làm, tỉ lệ đúng thấp. Sắp theo tỉ lệ đúng tăng dần
  // — người học muốn thấy phần yếu nhất trước.
  const weakest = topics
    .filter((t) => t.attempts > 0 && t.accuracy < 80)
    .sort((a, b) => a.accuracy - b.accuracy)[0]

  const practised = topics.filter((t) => t.attempts > 0).sort((a, b) => b.attempts - a.attempts)
  const today = todayAnswered(byDay)

  return (
    <Shell>
      <Screen>
        {/* Dòng chào là CỬA VÀO màn tài khoản — dòng này vốn là chữ tĩnh, bấm
            không được, nên không ai tìm ra chỗ đổi mật khẩu. Làm thành nút thay
            vì dựng thêm một thanh menu chỉ để dùng một chỗ. */}
        <button onClick={onGoAccount} className="-mx-1 flex w-auto cursor-pointer items-center gap-1 px-1 text-left">
          <Note>Chào {user}</Note>
          <Icon name="chevronRight" size={13} className="text-ink-faint" />
        </button>

        <div className="card my-3 space-y-2">
          <Between>
            <span className="text-sm font-semibold">Mục tiêu hôm nay</span>
            <span className="font-mono text-sm font-semibold text-gold">
              {today}/{DAILY_GOAL}
            </span>
          </Between>
          <Bar percent={(today / DAILY_GOAL) * 100} tone="bg-gold" />
          <Note className="!text-xs">
            {DAILY_GOAL} câu mỗi ngày. Đều đặn hơn ngày nào làm nhiều.
          </Note>
        </div>

        <button onClick={onPractice} className="btn btn-primary mb-3">
          Ôn luyện ngay
          <Icon name="arrow" size={18} />
        </button>

        {/* `mb-3` = 12px. Mockup để margin-bottom 10px ở cỡ chữ 11.5px, quy
            đổi theo thang thật thì ~13px. `mb-2` (8px) khiến thẻ dính vào
            nhãn "Chủ đề nổi bật" ngay dưới. */}
        {dueForReview > 0 && (
          <div className="card mb-3 border-transparent bg-bad-soft">
            <div className="text-sm font-semibold text-bad">Cần ôn lại</div>
            <Between className="mt-1">
              <span>{dueForReview} câu đã làm sai</span>
              {/* Ra DANH SÁCH câu sai, không phải ra màn chọn đề mục: bấm ở đây
                  người dùng muốn xem câu nào sai, không phải làm lại ngẫu nhiên. */}
              <button onClick={onGoWrong} className="text-sm font-semibold text-bad">
                Ôn ngay ›
              </button>
            </Between>
          </div>
        )}

        <Label>Chủ đề nổi bật</Label>
        {/* space-y-3 khớp màn Tiến độ. Cùng một component TopicLine mà hai
            màn dùng hai nhịp khác nhau thì danh sách "nhảy" khi chuyển tab —
            cùng một đề mục phải trông giống nhau ở mọi nơi. */}
        <div className="mt-1.5 space-y-3">
          {practised.slice(0, 3).map((t) => (
            <TopicLine key={t.slug} t={t} />
          ))}
          {practised.length === 0 && <Note>Chưa luyện gì. Bắt đầu bằng một chủ đề bất kỳ.</Note>}
        </div>

        <Label className="mt-6">Thi thử gần đây</Label>
        {weakest && (
          <Between className="border-t border-ink/10 py-2 text-sm">
            <span>{weakest.name}</span>
            <span className="text-ink-soft">{accText(weakest.accuracy)} đúng</span>
          </Between>
        )}
        <Between className="border-t border-ink/10 py-2 text-sm">
          <span>Xem toàn bộ tiến độ</span>
          <button onClick={onGoProgress} className="text-accent">
            Mở ›
          </button>
        </Between>

        {/* Lối vào màn gia hạn. Đặt ở cuối danh sách, cùng kiểu với dòng trên:
            nút phụ trong danh sách, không dựng thêm một màn menu. */}
        <Between className="border-t border-ink/10 py-2 text-sm">
          <span>Gia hạn quyền dùng</span>
          <button onClick={onGoPay} className="text-accent">
            Mở ›
          </button>
        </Between>
      </Screen>
    </Shell>
  )
}

/** Một dòng đề mục: tên + tỉ lệ đúng + thanh. Dùng ở Home, Chọn chủ đề và
 *  Tiến độ — cùng một cách hiển thị, không để mỗi màn tự vẽ. */
export function TopicLine({ t, showCount }: { t: Topic; showCount?: boolean }) {
  const tone = accTone(t.accuracy)
  return (
    <div>
      <Between className="text-base">
        {/* min-w-0: tên dài PHẢI được xuống dòng. Không có nó thì flex co
            cả hai vế và tên bị cắt bằng dấu chấm lửng.
            shrink-0 + nowrap trên vế phải: con số KHÔNG được xuống dòng. Tên
            "Hệ sinh dục, Vú và Sức khoẻ sinh sản" dài hơn cột, nếu không
            chặn thì "câu" trong "48% · 29 câu" rơi xuống dòng hai và đè lên
            thanh của đề mục kế tiếp. */}
        <span className={`min-w-0 font-semibold ${tone.text}`}>{t.name}</span>
        <span className={`shrink-0 font-mono text-xs whitespace-nowrap ${tone.text}`}>
          {showCount && t.attempts > 0 ? `${accText(t.accuracy)} · ${t.attempts} câu` : accText(t.accuracy)}
        </span>
      </Between>
      {/* mt-1: khoảng hở 4px giữa dòng tên và thanh.
          Mockup dùng margin-top ÂM (-5px) để thanh dính sát nhãn. Ở 12px thì
          đúng, nhưng ở 16px dòng chữ cao hơn, kéo vào âm là chữ bị dính vào
          thanh — đọc như một khối lẫn lộn. Tách ra 4px, phần nhóm lại do
          khoảng cách 12px giữa các khối đảm nhiệm. */}
      <Bar percent={t.accuracy} tone={tone.bar} className="mt-1" />
    </div>
  )
}

/** Số câu đã làm HÔM NAY.
 *
 *  Trước đây đây là hàm rỗng trả về 0 — biểu ngữ "Mục tiêu hôm nay 0/20" hiện
 *  vĩnh viễn 0% dù người dùng vừa làm 200 câu. Tệ hơn số sai: một con số luôn
 *  bằng 0 trông như app hỏng, và người học tưởng mình chưa làm gì.
 *
 *  Lấy từ `by_day` của `/progress` — cùng nguồn với biểu đồ "30 ngày qua" nên
 *  hai chỗ không thể lệch nhau.
 *
 *  PHẦN TỬ CUỐI là hôm nay: truy vấn `GetDailyHistory` sinh chuỗi ngày bằng
 *  `generate_series` kết thúc tại `CURRENT_DATE AT TIME ZONE 'Asia/Ho_Chi_Minh'`
 *  và tăng dần, nên ngày cuối luôn là hôm nay theo giờ Việt Nam — không cần tự
 *  tính ngày ở client, vì client có thể lệch múi giờ với server.
 *
 *  Mảng rỗng (chưa tải xong, hoặc mất mạng) trả 0 chứ không phải NaN.
 */
function todayAnswered(byDay: DayStat[]): number {
  if (byDay.length === 0) return 0
  return byDay[byDay.length - 1].answered
}

const ALL = { slug: '', name: 'Tất cả chủ đề' } as Topic

/* ------------------------------------------------------------------ thi thử */

/** Cấu hình đề thi thử.
 *
 *  `perQ` = số giây dành cho MỘT câu. Dùng để tính thời gian từ số câu thay vì
 *  gõ tay, vì người dùng chỉ chọn "20 câu" chứ ít khi chọn cả "20 câu, 18 phút"
 *  — và mỗi bộ riêng sẽ lệch nhau một cách vô nghĩa.
 *
 *  90 giây/câu là mức người làm thật: đọc tình huống, đọc 4 phương án, chọn,
 *  và có chút dự phòng. 60 giây thì hơi gấp với câu tình huống dài.
 */
const EXAM_PRESETS = [
  { count: 20, perQ: 90 },
  { count: 50, perQ: 90 },
  { count: 100, perQ: 75 },
]

/** Chọn loại đề. MVP chỉ có đề rút gọn — đề đầy đủ 280 câu/2 phần/6 giờ cần
 *  logic chọn câu theo 17 trọng số QĐ-22, nặng hơn nhiều bản này.
 *
 *  Đề đầy đủ vẫn hiện, khoá, để người dùng biết nó sẽ tới — thiếu nó thì tab
 *  "Thi thử" trông như chưa làm xong. */
export function ChooseExamKind({ onPick, onBack }: { onPick: () => void; onBack: () => void }) {
  return (
    <Shell>
      <Screen>
        <TopBar title="Chọn loại đề thi thử" onBack={onBack} />
        <h1 className="mb-2">Đề rút gọn</h1>
        <div className="card mb-3">
          <Between className="mb-1">
            <span className="font-semibold">Đề rút gọn</span>
            <span className="chip">MVP</span>
          </Between>
          <Note>
            Tuỳ chọn số câu và thời gian — dùng để luyện phản xạ làm bài.
          </Note>
        </div>

        <div className="card mb-4 opacity-60">
          <Between className="mb-1">
            <span className="font-semibold">Đề đầy đủ — Test Blueprint</span>
            <span className="badge">Sắp có</span>
          </Between>
          <Note>280 câu · 2 phần · 6 giờ, đúng cấu trúc QĐ-22/HĐYKQG</Note>
        </div>

        <button onClick={onPick} className="btn btn-primary">
          Tiếp tục với đề rút gọn
          <Icon name="arrow" size={18} />
        </button>
      </Screen>
    </Shell>
  )
}

/** Chọn số câu và thời gian cho đề rút gọn. */
export function ExamSetup({ onStart, onBack }: { onStart: (count: number, sec: number) => void; onBack: () => void }) {
  const [i, setI] = useState(0)
  const p = EXAM_PRESETS[i]
  const minutes = Math.round((p.count * p.perQ) / 60)

  return (
    <Shell>
      <Screen>
        <TopBar title="Cấu trúc đề" onBack={onBack} right={<span className="chip">Đề rút gọn</span>} />

        <h1 className="mb-1.5">Bao nhiêu câu?</h1>
        <Note className="mb-6">
          Thời gian tính sẵn theo số câu: mỗi câu 90 giây, làm tròn ra phút.
        </Note>

        <div className="space-y-2">
          {EXAM_PRESETS.map((pr, k) => (
            <button
              key={pr.count}
              onClick={() => setI(k)}
              className={
                'btn justify-between px-4 ' +
                (i === k ? 'btn-primary' : 'btn-ghost border border-ink/15')
              }
            >
              <span>{pr.count} câu</span>
              <span className="font-mono text-sm opacity-80">
                {Math.round((pr.count * pr.perQ) / 60)} phút
              </span>
            </button>
          ))}
        </div>

        <div className="card mt-5">
          <Between className="mb-1">
            <span className="text-sm font-semibold">Tổng thời gian</span>
            <span className="mono font-semibold">
              {Math.floor(minutes / 60)}:{String(minutes % 60).padStart(2, '0')}
            </span>
          </Between>
          <Note className="!text-xs">
            Bài nộp muộn vẫn được chấm đầy đủ, chỉ bị ghi nhận là nộp trễ hạn. Mất
            mạng lúc đang làm không mất đáp án — app gửi lại được khi có mạng.
          </Note>
        </div>

        <button onClick={() => onStart(p.count, p.count * p.perQ)} className="btn btn-gold mt-5">
          Bắt đầu thi
          <Icon name="arrow" size={18} />
        </button>
      </Screen>
    </Shell>
  )
}

export function ChooseTopic({
  topics,
  loading,
  error,
  onPick,
  onBack,
}: {
  topics: Topic[]
  loading: boolean
  error: string
  onPick: (t: Topic) => void
  onBack: () => void
}) {
  const usable = topics.filter((t) => !t.empty).length
  const soon = topics.filter((t) => t.empty).length

  return (
    <Shell>
      <Screen>
        <TopBar
          title="Chọn chủ đề"
          onBack={onBack}
          right={
            <button onClick={() => onPick(ALL)} className="rounded-lg px-2 py-1 text-sm text-accent">
              Tất cả
            </button>
          }
        />

        {error && <div className="mb-3 rounded-lg bg-bad-soft px-3 py-2 text-sm text-bad">{error}</div>}

        <Note className="mb-3">
          {loading ? 'Đang tải…' : `${usable} chủ đề đang có câu · ${soon} chủ đề còn đang bổ sung`}
        </Note>

        <div className={'space-y-2 ' + (loading ? 'opacity-50 pointer-events-none' : '')}>
          {topics.map((t) => (
            <Tile key={t.slug} onClick={() => onPick(t)} disabled={t.empty}>
              <Between className="mb-1.5">
                <span className="font-semibold">{t.name}</span>
                {t.empty ? (
                  <span className="badge">Sắp có</span>
                ) : t.attempts > 0 ? (
                  <span className={`font-mono text-xs ${accTone(t.accuracy).text}`}>
                    {accText(t.accuracy)}
                  </span>
                ) : (
                  <span className="chip">{t.available} câu</span>
                )}
              </Between>
              {!t.empty && <Bar percent={t.accuracy} tone={accTone(t.accuracy).bar} />}
            </Tile>
          ))}
        </div>
      </Screen>
    </Shell>
  )
}

export function ChooseCount({
  topic,
  onStart,
  onBack,
}: {
  topic: Topic | null
  onStart: (count: number) => void
  onBack: () => void
}) {
  const [count, setCount] = useState(20)
  const max = topic ? topic.available : 200
  // Không cho chọn nhiều hơn số câu thực có — server cũng sẽ chặn, nhưng để
  // người dùng bấm rồi mới nhận lỗi thì lãng phí một vòng gọi và dễ mất
  // tin tưởng. Giới hạn ngay tại chỗ.
  const ok = COUNTS.map((c) => c <= max)
  const disabled = !ok.some(Boolean)

  return (
    <Shell>
      <Screen>
        <TopBar
          title="Số câu"
          onBack={onBack}
          right={<span className="chip">{topic?.name ?? 'Tất cả chủ đề'}</span>}
        />

        <h1 className="mb-1.5">Hôm nay luyện bao nhiêu?</h1>
        <Note className="mb-6">
          {topic
            ? `${topic.name} có ${topic.available} câu trong kho của bạn.`
            : 'Tập câu mẫu miễn phí. Nhiều hơn cần quyền kho đầy đủ.'}
        </Note>

        <div className="space-y-2">
          {COUNTS.map((c, i) => (
            <button
              key={c}
              onClick={() => setCount(c)}
              disabled={!ok[i]}
              className={
                'btn ' +
                (count === c && ok[i]
                  ? 'btn-primary'
                  : 'btn-ghost border border-ink/15 hover:bg-canvas-2')
              }
            >
              {c} câu
              {!ok[i] && <span className="text-ink-faint"> (chỉ có {max})</span>}
            </button>
          ))}
        </div>

        <button onClick={() => onStart(count)} disabled={disabled} className="btn btn-primary mt-5">
          Bắt đầu
          <Icon name="arrow" size={18} />
        </button>
      </Screen>
    </Shell>
  )
}

/** Hook tải danh sách chủ đề, dùng chung cho Home và ChooseTopic để không
 *  gọi trùng. */
export function useTopics() {
  const [topics, setTopics] = useState<Topic[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  useEffect(() => {
    let alive = true
    api
      .topics()
      .then((r) => {
        if (alive) {
          setTopics(r.topics)
          setError('')
        }
      })
      .catch((e) => {
        if (alive) setError((e as ApiError).detail || 'Không tải được danh sách chủ đề.')
      })
      .finally(() => alive && setLoading(false))
    return () => {
      alive = false
    }
  }, [])

  return { topics, loading, error }
}





